// Mock Patient Demographic Service (PDS) API
// This is a placeholder for the actual PDS integration

export interface PDSNameSearchParams {
  givenName: string;
  familyName: string;
  gender: string;
  dateOfBirth: string;
  postcode?: string;
}

export interface PDSNHSSearchParams {
  nhsNumber: string;
  dateOfBirth: string;
}

export interface PDSPatient {
  nhsNumber: string;
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  gender: string;
  address: string;
  postcode: string;
  telephone?: string;
  email?: string;
  gpName?: string;
  gpSurgery?: string;
  gpSurgeryAddress?: string;
  title?: string;
  preferredAddress: any;
}

// Mock patient database for demonstration
const mockPDSDatabase: PDSPatient[] = [
  {
    nhsNumber: "9876543210",
    givenName: "John",
    familyName: "Smith",
    dateOfBirth: "1985-03-15",
    gender: "Male",
    address: "123 High Street, Bristol",
    postcode: "BS1 2AB",
    telephone: "01234567890",
    email: "john.smith@email.com",
    gpName: "Dr. Sarah Johnson",
    gpSurgery: "Bristol Medical Centre",
    gpSurgeryAddress: "45 Park Road, Bristol, BS2 8QR",
    preferredAddress: null,
  },
  {
    nhsNumber: "9876543211",
    givenName: "Emma",
    familyName: "Johnson",
    dateOfBirth: "1990-07-22",
    gender: "Female",
    address: "456 Queen's Road, Bristol",
    postcode: "BS8 1QU",
    telephone: "01234567891",
    email: "emma.johnson@email.com",
    gpName: "Dr. Michael Brown",
    gpSurgery: "Clifton Health Practice",
    gpSurgeryAddress: "12 Clifton Road, Bristol, BS8 2PR",
    preferredAddress: null,
  },
  {
    nhsNumber: "9876543212",
    givenName: "Michael",
    familyName: "Williams",
    dateOfBirth: "1978-11-05",
    gender: "Male",
    address: "789 Park Avenue, Bristol",
    postcode: "BS5 6NR",
    telephone: "01234567892",
    email: "michael.williams@email.com",
    gpName: "Dr. Lisa Anderson",
    gpSurgery: "Eastville Surgery",
    gpSurgeryAddress: "442-450 Stapleton Rd, Bristol, BS5 6NR",
    preferredAddress: null,
  },
  {
    nhsNumber: "9876543213",
    givenName: "Sarah",
    familyName: "Davis",
    dateOfBirth: "1995-04-18",
    gender: "Female",
    address: "321 Church Road, Bristol",
    postcode: "BS3 2DX",
    telephone: "01234567893",
    email: "sarah.davis@email.com",
    preferredAddress: null,
  },
  {
    nhsNumber: "9876543214",
    givenName: "James",
    familyName: "Brown",
    dateOfBirth: "2010-09-12",
    gender: "Male",
    address: "654 London Road, Bristol",
    postcode: "BS4 3PQ",
    telephone: "01234567894",
    gpName: "Dr. Emily White",
    gpSurgery: "Bedminster Family Practice",
    gpSurgeryAddress: "88 West Street, Bristol, BS3 3LG",
    preferredAddress: null,
  },
];

// Simulate API delay
const simulateDelay = (ms: number = 500) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Search PDS by name and demographics
 * @param params Name search parameters
 * @returns Array of matching patients (may be multiple matches)
 */
export async function searchPDSByName(
  params: PDSNameSearchParams,
): Promise<PDSPatient[]> {
  await simulateDelay(800); // Simulate network delay

  const { givenName, familyName, gender, dateOfBirth, postcode } = params;

  const results = mockPDSDatabase.filter((patient) => {
    const givenNameMatch = patient.givenName
      .toLowerCase()
      .includes(givenName.toLowerCase());
    const familyNameMatch = patient.familyName
      .toLowerCase()
      .includes(familyName.toLowerCase());
    const genderMatch = !gender || patient.gender === gender;
    const dobMatch = patient.dateOfBirth === dateOfBirth;
    const postcodeMatch =
      !postcode ||
      patient.postcode.toLowerCase().includes(postcode.toLowerCase());

    return (
      givenNameMatch &&
      familyNameMatch &&
      genderMatch &&
      dobMatch &&
      postcodeMatch
    );
  });

  return results;
}

/**
 * Search PDS by NHS Number
 * @param params NHS number search parameters
 * @returns Single patient or null
 */
export async function searchPDSByNHS(
  params: PDSNHSSearchParams,
): Promise<PDSPatient | null> {
  await simulateDelay(600); // Simulate network delay

  const { nhsNumber, dateOfBirth } = params;

  const patient = mockPDSDatabase.find(
    (p) => p.nhsNumber === nhsNumber && p.dateOfBirth === dateOfBirth,
  );

  return patient || null;
}

/**
 * Validate NHS Number format (basic validation)
 * Real NHS numbers are 10 digits
 */
export function validateNHSNumber(nhsNumber: string): boolean {
  return /^\d{10}$/.test(nhsNumber);
}

/**
 * Format NHS Number with spaces (XXX XXX XXXX)
 */
export function formatNHSNumber(nhsNumber: string): string {
  const cleaned = nhsNumber.replace(/\s/g, "");
  if (cleaned.length !== 10) return nhsNumber;

  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
}
